import React, { useCallback } from 'react';
import { View, ScrollView, Text, TouchableOpacity } from 'react-native';
import { styles } from './NetworkDevicesModal.styles';
import NetworkDeviceRow from './NetworkDeviceRow';
import ModalTitle from '../ModalTitle/ModalTitle';
import { useLocalDeviceStatus } from '../../../utils/localComms/hooks/useLocalDeviceStatus';
import { networkStatusSubject } from '../../../hooks/connectivity/useNetworkListener';
import useBehaviorSubjectState from '../../../hooks/rxjs/useSubjectState';
import { localComms } from '../../../utils/localComms';

interface Props {
  onClose: () => void;
}

const NetworkDevicesModal: React.FC<Props> = ({ onClose }) => {
  const sdkStatus = useLocalDeviceStatus();
  const { value: isConnected } = useBehaviorSubjectState(networkStatusSubject);

  const onRestartClicked = async () => {
    await localComms.restart();
  };
  const onExportLogClicked = async () => {
    localComms.shareLog();
    onClose();
  };

  const onExportDbClicked = async () => {
    localComms.exportDatabase();
    onClose();
  };

  const convertTimeStamp = useCallback((timestamp: string) => {
    const dateTimePart =
      timestamp.split(' ')[0] + 'T' + timestamp.split(' ')[1];
    const date = new Date(dateTimePart + 'Z');
    const options: Intl.DateTimeFormatOptions = {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true,
    };
    return date.toLocaleString('en-US', options);
  }, []);

  return (
    <View style={styles.container}>
      <ModalTitle title="POS Devices" onDismiss={onClose} />
      <View style={styles.content}>
        {(sdkStatus?.queue ?? 0) > 0 && (
          <View style={styles.message}>
            <Text testID="message-queueCount" style={styles.messageText}>
              {`There are ${
                sdkStatus?.queue ?? 0
              } updates waiting to be synced to the cloud!`}
            </Text>
          </View>
        )}
        {(sdkStatus?.nodes?.length ?? 0) > 0 ? (
          <ScrollView style={styles.list}>
            <View style={styles.rows}>
              {sdkStatus?.nodes?.map((device, i) => (
                <NetworkDeviceRow
                  key={i}
                  name={device.metadata?.name ?? device.nodeId}
                  lastSeen={convertTimeStamp(device.lastSeen)}
                  isAvailableInLocalNetwork={
                    sdkStatus.isLocalCommunicationAvailable === true
                      ? device.currentDevice === true
                        ? sdkStatus.isConnected === true &&
                          device.status === 'UP'
                        : device.status === 'UP'
                      : false
                  }
                  hasInternetConnection={
                    device.currentDevice === true ? isConnected : true
                  }
                  ipAddress={device.nodeId}
                  isCurrentDevice={device.currentDevice === true}
                  isCDSDevice={device.metadata?.appType === 'CDS'}
                />
              ))}
            </View>
          </ScrollView>
        ) : (
          <View style={styles.empty}>
            <Text style={styles.emptyText}>No devices found.</Text>
          </View>
        )}
        <View style={styles.actions}>
          <TouchableOpacity
            testID="btn-exportLogs"
            onPress={onExportLogClicked}
            style={styles.btn}
          >
            <Text style={styles.btnText}>Export Logs</Text>
          </TouchableOpacity>
          <TouchableOpacity
            testID="btn-exportDbToBackup"
            onPress={onExportDbClicked}
            style={styles.btn}
          >
            <Text style={styles.btnText}>Export DB to Backup</Text>
          </TouchableOpacity>
          <TouchableOpacity
            testID="btn-refreshServer"
            onPress={onRestartClicked}
            style={styles.btn}
          >
            <Text style={styles.btnText}>Refresh Server</Text>
          </TouchableOpacity>
        </View>
      </View>
    </View>
  );
};

export default NetworkDevicesModal;
